import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Manual } from "@arc/contracts";
import { cable, joinRoom } from "@/realtime/cable.js";
import type { AuthSession } from "@/auth/keycloak.js";

/** Mirrors the server's facility-room event (ADR-026). Identity-free by design — see `FacilityManualLock` on the API. */
interface FacilityManualLock {
  manualId: number;
  locked: boolean;
}

/**
 * Keep a facility's manual cards' padlocks live (ADR-026).
 *
 * Without this, a colleague opening a manual only shows up after the other person happens to reload — which is exactly
 * how it behaved before: two windows side by side, and the second had to be refreshed to see the first had taken the
 * manual.
 *
 * **It patches the cache in place rather than refetching.** The event already carries the one field that changed, so
 * invalidating would send every watcher's browser to re-fetch the whole list every time anyone opens or closes an
 * editor — a broadcast turning into an N-client request storm on a channel whose whole point was to avoid polling.
 */
export function useFacilityLockFeed(session: AuthSession | null, facilityId: number | null): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!session || facilityId === null) return;

    const onLock = (payload: FacilityManualLock): void => {
      // Patch EVERY cached variant of this facility's list (archived / not), since the same manual appears in whichever
      // one is currently mounted and a stale sibling would resurface on the next toggle.
      qc.setQueriesData<Manual[]>({ queryKey: ["facility-manuals", session.realm, session.sub, facilityId] }, (current) =>
        current?.some((m) => m.id === payload.manualId)
          ? current.map((m) => (m.id === payload.manualId ? { ...m, locked: payload.locked } : m))
          : current,
      );
    };

    const leave = joinRoom({ subscribe: "facility:subscribe", unsubscribe: "facility:unsubscribe" }, { facilityId });
    cable().on("facility:manual-lock", onLock);
    return () => {
      cable().off("facility:manual-lock", onLock);
      leave();
    };
  }, [qc, session, facilityId]);
}
